import { createRoute, createRouter, redirect } from '@tanstack/react-router'
import { rootRoute } from '@/app/routes/_root'
import { appShellRoute } from '@/app/routes/_appShell'
import {
  validateChannelOnly,
  validateChannelsSearch,
  validateClipCreatorSearch,
  validateSettingsSearch,
} from '@/app/routes/_search'
import { DesignSystemCatalog } from '@/design-system/DesignSystemCatalog'
import { analyticsRoute } from '@/features/analytics/routes'
import { loginRoute, passwordResetRoute, ssoCallbackRoute } from '@/features/auth/routes'
import { ChannelsPage } from '@/features/channels/pages/ChannelsPage'
import { factoryRoutes } from '@/features/factory/routes'
import { JobsPage } from '@/features/jobs/pages/JobsPage'
import { privacyRoute, termsRoute } from '@/features/legal/routes'
import { poolDetailRoute, poolsRoute } from '@/features/pools/routes'
import { indexRoute, projectDetailRoute } from '@/features/projects/routes'
import { rankingRoute } from '@/features/ranking/routes'
import { recoveryRoute } from '@/features/recovery/routes'
import { ReportsPage } from '@/features/reports/pages/ReportsPage'
import { SafetyPage } from '@/features/safety/pages/SafetyPage'
import { SchedulerPage } from '@/features/scheduler/pages/SchedulerPage'
import { SettingsPage } from '@/features/settings/pages/SettingsPage'
import { ToolsIndexPage } from '@/features/tools/pages/ToolsIndexPage'
import { ImageGenerationPage } from '@/features/tools/pages/ImageGenerationPage'
import { BackgroundRemovalPage } from '@/features/tools/pages/BackgroundRemovalPage'
import { YoutubeDownloaderPage } from '@/features/tools/pages/YoutubeDownloaderPage'
import { ClipCreatorPage } from '@/features/tools/pages/ClipCreatorPage'

// Re-export so existing imports of `RouterContext` from this module keep working.
export type { RouterContext } from '@/app/routes/_root'

// Simple "/api/v1-style" routes whose only quirk is forwarding `?channel=`.
// They'd be over-engineered as their own files — just one createRoute call
// each — so they live here next to the route tree composition.
const jobsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/jobs',
  validateSearch: validateChannelOnly,
  component: JobsPage,
})

const schedulerRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/scheduler',
  validateSearch: validateChannelOnly,
  component: SchedulerPage,
})

const safetyRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/safety',
  validateSearch: validateChannelOnly,
  component: SafetyPage,
})

const reportsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/reports',
  validateSearch: validateChannelOnly,
  component: ReportsPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/settings',
  validateSearch: validateSettingsSearch,
  component: SettingsPage,
})

// Also the landing page of the OAuth callbacks, which add the outcome to the
// search (``platform``/``result``, or the legacy ``error``) for the page to show.
const channelsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/channels',
  validateSearch: validateChannelsSearch,
  component: ChannelsPage,
})

// The notification settings became the "Notificações" tab of /settings; the
// old path keeps working for links and bookmarks.
const notificationsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/notifications',
  validateSearch: validateChannelOnly,
  beforeLoad: ({ search }) => {
    // TanStack Router uses thrown ``redirect()`` objects (not Errors) as its
    // control-flow signal — same as the appShell guard.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/settings', search: { ...search, tab: 'notifications' } })
  },
})

const toolsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/tools',
  validateSearch: validateChannelOnly,
  component: ToolsIndexPage,
})

const toolsImageGenerationRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/tools/image-generation',
  validateSearch: validateChannelOnly,
  component: ImageGenerationPage,
})

const toolsBackgroundRemovalRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/tools/background-removal',
  validateSearch: validateChannelOnly,
  component: BackgroundRemovalPage,
})

const toolsYoutubeDownloadRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/tools/youtube-download',
  validateSearch: validateChannelOnly,
  component: YoutubeDownloaderPage,
})

const toolsClipCreatorRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/tools/clip-creator',
  validateSearch: validateClipCreatorSearch,
  component: ClipCreatorPage,
})

const designSystemRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/internal/design-system',
  component: DesignSystemCatalog,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  passwordResetRoute,
  ssoCallbackRoute,
  termsRoute,
  privacyRoute,
  appShellRoute.addChildren([
    indexRoute,
    projectDetailRoute,
    ...factoryRoutes,
    jobsRoute,
    schedulerRoute,
    poolsRoute,
    poolDetailRoute,
    safetyRoute,
    rankingRoute,
    analyticsRoute,
    reportsRoute,
    settingsRoute,
    channelsRoute,
    recoveryRoute,
    notificationsRoute,
    toolsRoute,
    toolsImageGenerationRoute,
    toolsBackgroundRemovalRoute,
    toolsYoutubeDownloadRoute,
    toolsClipCreatorRoute,
    designSystemRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  // Placeholder context — real value comes from `<RouterProvider context>` in
  // App.tsx, which injects the live Clerk auth snapshot. Routes' `beforeLoad`
  // hooks read `context.auth` from there. The non-null assertion is the
  // documented TanStack pattern for context types that fill at provider time.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  context: { auth: undefined! },
  defaultPreload: 'intent', // prefetch linked routes on hover — "fluidez"
  defaultPreloadStaleTime: 0,
  // Wraps every navigation in ``document.startViewTransition`` when the
  // browser supports it (Chrome, Edge). Pairs with the
  // ::view-transition-{old,new} CSS rules in styles/globals.scss for a
  // 180ms cross-fade. Firefox just navigates instantly — graceful
  // fallback, no JS overhead.
  defaultViewTransition: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
